/**
 * Command Definitions - Single Source of Truth for Keyboard Shortcuts
 * Feature: 051-keybindings
 *
 * All commands and their keybindings are defined here.
 * Components use useCommandHandler to subscribe to commands.
 */

import { isMac } from '../keyboard';

export interface CommandKeybinding {
  key: string;
  meta?: boolean;   // Mac: Cmd key
  ctrl?: boolean;   // Ctrl key
  alt?: boolean;    // Alt key
  shift?: boolean;  // Shift key
}

export interface CommandDefinition {
  id: string;
  label: string;
  description?: string;
  category?: string;
  keybinding: CommandKeybinding;
}

/**
 * All command definitions
 * Use 'as const satisfies' pattern for type inference
 */
export const COMMANDS = {
  // ============================================================================
  // Critique Commands
  // ============================================================================

  'critique.submit': {
    id: 'critique.submit',
    label: 'Submit Critique',
    description: 'Post your comment',
    category: 'critique',
    keybinding: {
      key: 'Enter',
      meta: isMac(),
      ctrl: !isMac(),
    }
  },

  'critique.suggestEdit': {
    id: 'critique.suggestEdit',
    label: 'Suggest Edit',
    description: 'Open edit suggestion dialog',
    category: 'critique',
    keybinding: {
      key: 'e',
      meta: isMac(),
      alt: !isMac(),
    }
  },

  'critique.addMarkup': {
    id: 'critique.addMarkup',
    label: 'Add Markup',
    description: 'Open markup annotation dialog',
    category: 'critique',
    keybinding: {
      key: 'm',
      meta: isMac(),
      alt: !isMac(),
    }
  },

  // ============================================================================
  // Navigation Commands - Primary keybindings (Left/Right arrows)
  // ============================================================================

  'critique.nextPhoto': {
    id: 'critique.nextPhoto',
    label: 'Next Photo',
    description: 'Navigate to next photo in collection',
    category: 'navigation',
    keybinding: {
      key: 'ArrowRight',
      meta: isMac(),
      alt: !isMac(),
    }
  },

  'critique.prevPhoto': {
    id: 'critique.prevPhoto',
    label: 'Previous Photo',
    description: 'Navigate to previous photo in collection',
    category: 'navigation',
    keybinding: {
      key: 'ArrowLeft',
      meta: isMac(),
      alt: !isMac(),
    }
  },

  // ============================================================================
  // Navigation Commands - Alternative keybindings (Up/Down arrows)
  // ============================================================================

  'critique.nextPhoto.down': {
    id: 'critique.nextPhoto',  // Same command ID as ArrowRight
    label: 'Next Photo',
    description: 'Navigate to next photo in collection',
    category: 'navigation',
    keybinding: {
      key: 'ArrowDown',
      meta: isMac(),
      alt: !isMac(),
    }
  },

  'critique.prevPhoto.up': {
    id: 'critique.prevPhoto',  // Same command ID as ArrowLeft
    label: 'Previous Photo',
    description: 'Navigate to previous photo in collection',
    category: 'navigation',
    keybinding: {
      key: 'ArrowUp',
      meta: isMac(),
      alt: !isMac(),
    }
  },

  // ============================================================================
  // View Mode Commands (Feature 057-lightbox-fullscreen-oneup)
  // ============================================================================

  'viewMode.toggleDimmedLights': {
    id: 'viewMode.toggleDimmedLights',
    label: 'Toggle Dimmed Lights',
    description: 'Dim surrounding UI while keeping photos at full brightness (Lightroom "Lights Out")',
    category: 'viewMode',
    keybinding: {
      key: 'l',
    }
  },

  'viewMode.toggleFullscreen': {
    id: 'viewMode.toggleFullscreen',
    label: 'Toggle Fullscreen',
    description: 'Enter/exit browser fullscreen mode with photos on black background',
    category: 'viewMode',
    keybinding: {
      key: 'f',
    }
  },

  'viewMode.exitMode': {
    id: 'viewMode.exitMode',
    label: 'Exit View Mode',
    description: 'Return to normal view from dimmed lights or fullscreen',
    category: 'viewMode',
    keybinding: {
      key: 'Escape',
    }
  },

  // ============================================================================
  // Help Commands
  // ============================================================================

  'help.showKeyboardShortcuts': {
    id: 'help.showKeyboardShortcuts',
    label: 'Show Keyboard Shortcuts',
    description: 'Display keyboard shortcuts help modal',
    category: 'help',
    keybinding: {
      key: '?',
      shift: true,  // Shift+/ produces '?'
    }
  },

} as const satisfies Record<string, CommandDefinition>;

/**
 * Type-safe union of all command IDs
 * Derived from actual command definitions
 *
 * Example: 'critique.submit' | 'critique.nextPhoto' | 'critique.prevPhoto' | ...
 */
export type CommandId = typeof COMMANDS[keyof typeof COMMANDS]['id'];

/**
 * Get a command definition by ID (type-safe)
 */
export function getCommand(id: CommandId): CommandDefinition | undefined {
  const entry = Object.values(COMMANDS).find(cmd => cmd.id === id);
  return entry;
}

/**
 * Get all keybindings for a command ID
 * Useful when a command has multiple keybinding variants
 */
export function getKeybindings(id: CommandId): CommandKeybinding[] {
  return Object.values(COMMANDS)
    .filter(cmd => cmd.id === id)
    .map(cmd => cmd.keybinding);
}

/**
 * Get all commands in a category
 */
export function getCommandsByCategory(category: string): CommandDefinition[] {
  return Object.values(COMMANDS).filter(cmd => cmd.category === category);
}

/**
 * Format a keybinding for display
 * Example: { key: 'Enter', meta: true } -> 'Cmd+Enter' (on Mac)
 */
export function formatKeybinding(keybinding: CommandKeybinding): string {
  const modifiers: string[] = [];

  if (keybinding.meta) modifiers.push(isMac() ? 'Cmd' : 'Win');
  if (keybinding.ctrl) modifiers.push('Ctrl');
  if (keybinding.alt) modifiers.push('Alt');
  if (keybinding.shift) modifiers.push('Shift');

  modifiers.push(keybinding.key);

  return modifiers.join('+');
}
